import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';

import { nextPage } from '@mds/shared/lib/pagination';

import * as eventsApi from './events.api';
import type { GuestEventQuery } from './events.api';

/** Four rows of three on a desktop; a comfortable scroll on a phone. */
export const EVENTS_PAGE_SIZE = 12;

/**
 * Every event query key, built in one place. The list key carries the filters,
 * so each category / pricing / search combination is cached on its own.
 */
export const eventKeys = {
  all: ['events'] as const,
  list: (query: GuestEventQuery) => [...eventKeys.all, 'list', query] as const,
  infinite: (filters: Omit<GuestEventQuery, 'page' | 'limit'>) =>
    [...eventKeys.all, 'infinite', filters] as const,
  detail: (eventIdentifier: string) => [...eventKeys.all, 'detail', eventIdentifier] as const,
};

export const eventQueries = {
  /** Filtering is server-side; the previous page stays on screen while the next one loads. */
  list: (query: GuestEventQuery = {}) =>
    queryOptions({
      queryKey: eventKeys.list(query),
      queryFn: () => eventsApi.listEvents(query),
      placeholderData: keepPreviousData,
    }),
  /**
   * The programme a page at a time, for `<InfiniteCardList>`. The filters are
   * the key, so changing one starts again from page 1.
   */
  infinite: (filters: Omit<GuestEventQuery, 'page' | 'limit'> = {}) =>
    infiniteQueryOptions({
      queryKey: eventKeys.infinite(filters),
      queryFn: ({ pageParam }) =>
        eventsApi.listEvents({ ...filters, page: pageParam, limit: EVENTS_PAGE_SIZE }),
      initialPageParam: 1,
      getNextPageParam: nextPage,
    }),
  detail: (eventIdentifier: string) =>
    queryOptions({
      queryKey: eventKeys.detail(eventIdentifier),
      queryFn: () => eventsApi.getEvent(eventIdentifier),
    }),
};
